import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Button } from '@mui/material';
import PageBlock from '@sharedCR/common/PageBlock';
import DataDisplay, {
  useResponsiveDataDisplayVariant,
} from '@sharedCR/coreDesignSystem/DataDisplay';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { selectProcessesUsedByOwner, selectRunsByOwner } from 'store/aiProcessRuns';

import { PAGE_TITLE } from '../AiActivity.constants';
import { AiActivityTestIds } from '../AiActivity.testIds';
import {
  computeOverviewMetrics,
  formatDurationSeconds,
  formatRelativeTime,
  resolveProcessName,
} from '../AiActivity.utils';

import { OVERVIEW_TILE_CONFIG, OverviewTileKey } from './OverviewCard.config';
import {
  OVERVIEW_NO_DATA_TEXT,
  OVERVIEW_ZERO_COUNT_TEXT,
  START_BUTTON_LABEL,
} from './OverviewCard.constants';
import { tileCardStyles, tilesRowStyles } from './OverviewCard.styles';

const OverviewCard = ({ ownerId, isStartAllowed = false, onStartAiProcessClick = null }) => {
  const runs = useSelector(selectRunsByOwner(ownerId));
  const processes = useSelector(selectProcessesUsedByOwner(ownerId));

  const processesById = useMemo(
    () => Object.fromEntries((processes ?? []).map(item => [item.id, item])),
    [processes],
  );

  const dataDisplayVariant = useResponsiveDataDisplayVariant();

  const metrics = useMemo(() => computeOverviewMetrics(runs), [runs]);

  const tileValuesByKey = useMemo(() => {
    const workingValue =
      metrics.liveRunsCount > 0 ? `${metrics.liveRunsCount}` : OVERVIEW_ZERO_COUNT_TEXT;

    const lastActivityProcessName = metrics.lastActivityProcessId
      ? resolveProcessName({ aiProcessId: metrics.lastActivityProcessId }, processesById)
      : null;

    const lastActivityValue = metrics.lastActivityAt
      ? `${formatRelativeTime(metrics.lastActivityAt)}${
          lastActivityProcessName ? ` · ${lastActivityProcessName}` : ''
        }`
      : OVERVIEW_NO_DATA_TEXT;

    const needsAttentionValue =
      metrics.needsAttentionCount > 0 ? `${metrics.needsAttentionCount}` : OVERVIEW_ZERO_COUNT_TEXT;

    const avgTimeValue = _.isNil(metrics.avgSeconds)
      ? OVERVIEW_NO_DATA_TEXT
      : formatDurationSeconds(metrics.avgSeconds);

    return {
      [OverviewTileKey.WORKING_NOW]: workingValue,
      [OverviewTileKey.LAST_ACTIVITY]: lastActivityValue,
      [OverviewTileKey.NEEDS_ATTENTION]: needsAttentionValue,
      [OverviewTileKey.AVG_TIME]: avgTimeValue,
    };
  }, [metrics, processesById]);

  const actionContent = useMemo(() => {
    if (!isStartAllowed || !onStartAiProcessClick) {
      return null;
    }

    return (
      <Button
        data-testid={AiActivityTestIds.START_AI_PROCESS_BUTTON}
        size="small"
        variant="contained"
        onClick={onStartAiProcessClick}
      >
        {START_BUTTON_LABEL}
      </Button>
    );
  }, [isStartAllowed, onStartAiProcessClick]);

  return (
    <PageBlock actionContent={actionContent} title={PAGE_TITLE} withAction={!!actionContent}>
      <Box data-testid={AiActivityTestIds.OVERVIEW_CARD} sx={tilesRowStyles}>
        {OVERVIEW_TILE_CONFIG.map(tile => (
          <Box key={tile.key} sx={tileCardStyles}>
            <DataDisplay
              icon={<tile.Icon />}
              iconColor={tile.getIconColor(metrics)}
              title={tile.title}
              value={tileValuesByKey[tile.key]}
              variant={dataDisplayVariant}
            />
          </Box>
        ))}
      </Box>
    </PageBlock>
  );
};

OverviewCard.propTypes = {
  isStartAllowed: PropTypes.bool,
  ownerId: PropTypes.string,
  onStartAiProcessClick: PropTypes.func,
};

export default memo(OverviewCard);
